import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { bookingsAPI, paymentsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { formatPrice, formatDate } from '../lib/utils';
import { Loader2, CreditCard, Shield, CheckCircle, ArrowLeft } from 'lucide-react';

export default function CheckoutPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const loadBooking = async () => {
      try {
        const response = await bookingsAPI.getById(bookingId);
        setBooking(response.data);
        
        // If already paid, redirect to success
        if (response.data.payment_status === 'paid') {
          navigate('/payment/success');
        }
      } catch (error) {
        console.error('Error loading booking:', error);
        toast.error('Failed to load booking details');
        navigate('/bookings');
      } finally {
        setLoading(false);
      }
    };
    loadBooking();
  }, [bookingId, isAuthenticated, navigate]);

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const response = await paymentsAPI.createCheckout({
        booking_id: bookingId,
        origin_url: window.location.origin,
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate checkout');
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-semibold">Booking not found</h1>
          <Button onClick={() => navigate('/bookings')} className="mt-4">
            View My Bookings
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-12" data-testid="checkout-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="gap-2 mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Order Summary */}
          <Card className="p-6 rounded-2xl h-fit" data-testid="order-summary">
            <h2 className="font-heading text-2xl font-semibold mb-6">Order Summary</h2>
            
            <div className="space-y-4">
              <div className="pb-4 border-b">
                <h3 className="font-semibold text-lg mb-2">{booking.tour_title}</h3>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>Date: {formatDate(booking.date)}</p>
                  <p>Time: {booking.time}</p>
                  <p>Guests: {booking.guests}</p>
                </div>
              </div>

              {booking.special_requests && (
                <div className="pb-4 border-b">
                  <p className="text-sm font-medium">Special Requests:</p>
                  <p className="text-sm text-muted-foreground">{booking.special_requests}</p>
                </div>
              )}

              <div className="flex justify-between items-center pt-2">
                <span className="text-lg">Total</span>
                <span className="font-heading text-2xl font-bold text-primary" data-testid="checkout-total">
                  {formatPrice(booking.total_price)}
                </span>
              </div>
            </div>
          </Card>

          {/* Payment Section */}
          <Card className="p-6 rounded-2xl" data-testid="payment-section">
            <h2 className="font-heading text-2xl font-semibold mb-6">Complete Payment</h2>
            
            <div className="space-y-6">
              {/* Trust Badges */}
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                <Shield className="h-6 w-6 text-primary" />
                <div>
                  <p className="font-medium text-sm">Secure Payment</p>
                  <p className="text-xs text-muted-foreground">
                    Your payment info is protected with Stripe
                  </p>
                </div>
              </div>

              {/* What you get */}
              <div className="space-y-2">
                <p className="font-medium text-sm">What's included:</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Instant booking confirmation
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Free cancellation up to 24 hours
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Best price guarantee
                  </li>
                </ul>
              </div>

              {/* Pay Button */}
              <Button 
                className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full py-6 text-lg font-semibold"
                onClick={handleCheckout}
                disabled={checkoutLoading}
                data-testid="pay-btn"
              >
                {checkoutLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5 mr-2" />
                    Pay {formatPrice(booking.total_price)}
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                By clicking "Pay", you agree to our Terms of Service and Privacy Policy.
              </p>
            </div>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
}
